import { useCallback, useEffect, useState, type ReactNode } from "react";

import { ArrowLeft, ChevronRight, Eye } from "lucide-react";

import type { CycleHistoryRow } from "./CycleHistoryPanel";
import LeaderboardSnapshotView, {
  type LeaderboardSnapshotPayload,
} from "./LeaderboardSnapshotView";
import SponsorDrilldownPanel from "./SponsorDrilldownPanel";
import SponsorPollResponseContent, {
  getPollResponseTitle,
  type PollResponseTarget,
} from "./SponsorPollResponseContent";
import Button from "../../Components/Button";
import {
  HubPageLoading,
  hubModalLoadingClassName,
} from "../../Components/EngagementEmptyPanel";
import Modal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchCycleLeaderboard,
  fetchCycleResponses,
} from "../../Store/Reducers/SponsorEngagementSlice";
import { POLL_HISTORY_TABLE } from "../../Utils/Helper/discoveryHubUi";

type PollHistoryModalsProps = {
  row: CycleHistoryRow | null;
  modal: "responses" | "leaderboard" | null;
  onClose: () => void;
};

type SponsorResponseRow = {
  sponsor_id?: string;
  cycle_id?: string;
  sponsor_name?: string;
  theater?: string;
  completed_accounts?: number;
  total_accounts?: number;
  response_status?: string;
};

type TeamResponseRow = {
  team_member_id?: string;
  team_member_name?: string;
  sponsor_id?: string;
  sponsor_name?: string;
  cycle_id?: string;
  completed_accounts?: number;
  total_accounts?: number;
  response_status?: string;
};

type DrilldownTarget =
  | {
      kind: "poll1";
      sponsorId: string;
      cycleId: string;
      period?: string;
      sponsorName?: string;
    }
  | {
      kind: "poll2";
      sponsorId: string;
      cycleId: string;
      period?: string;
      teamMemberId?: string;
      teamMemberName?: string;
      sponsorName?: string;
    };

/** Fixed shell for View Responses list → sponsor detail (avoids width/height jumps). */
const RESPONSES_MODAL_SIZE = "4xl";
/** Shared fixed height for Poll History response + drilldown shells. */
const POLL_HISTORY_MODAL_SHELL_CLASS = "h-[85vh] min-h-[85vh] max-h-[90vh]";
const RESPONSES_MODAL_BODY_CLASS =
  "overflow-hidden flex flex-col flex-1 min-h-0";

/** Sponsor drilldown inline view (Poll History) — screen-2xl, not View Responses size. */
const DRILLDOWN_MODAL_BODY_CLASS =
  "overflow-hidden flex flex-col flex-1 min-h-0 !p-0";

const ResponsesModalScroll = ({ children }: { children: ReactNode }) => (
  <div className="flex h-full min-h-0 flex-1 flex-col">
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto pr-1">
      {children}
    </div>
  </div>
);

const ResponsesModalCentered = ({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "error";
}) => (
  <div
    className={`flex h-full min-h-0 flex-1 items-center justify-center px-4 py-8 text-center text-sm ${
      tone === "error" ? "text-red-600" : "text-gray-500"
    }`}
  >
    {children}
  </div>
);

const statusLabel = (status?: string) => {
  switch (status) {
    case "submitted":
      return "Submitted";
    case "late_window":
      return "Late window";
    case "not_responded":
      return "Not responded";
    default:
      return "Pending";
  }
};

const ResponseStatusCell = ({
  status,
  canViewResponses,
  onViewResponses,
  showDrilldown,
  canDrilldown,
  drilldownLabel,
  onDrilldown,
}: {
  status?: string;
  canViewResponses: boolean;
  onViewResponses: () => void;
  showDrilldown?: boolean;
  canDrilldown?: boolean;
  drilldownLabel?: string;
  onDrilldown?: () => void;
}) => (
  <td>
    <div className="flex items-center gap-1.5 flex-wrap min-w-[9rem]">
      <span className="text-gray-700 text-xs mr-1">{statusLabel(status)}</span>
      {/* Primary: view this sponsor's individual poll response */}
      <button
        type="button"
        className="inline-flex items-center gap-1 shrink-0 rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-40 disabled:pointer-events-none transition-colors"
        disabled={!canViewResponses}
        aria-label="View response"
        title="View response"
        onClick={onViewResponses}
      >
        <Eye size={11} />
        View Response
      </button>
      {/* Secondary (Poll 1 only): open this sponsor's engagement drilldown */}
      {showDrilldown && (
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-0.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          disabled={!canDrilldown}
          aria-label={drilldownLabel}
          onClick={onDrilldown}
          title="View drilldown"
        >
          <span className="text-[11px] font-medium">Drilldown</span>
          <ChevronRight size={14} strokeWidth={2.25} />
        </button>
      )}
    </div>
  </td>
);

const PollHistoryModals = ({ row, modal, onClose }: PollHistoryModalsProps) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  const [pollResponseTarget, setPollResponseTarget] =
    useState<PollResponseTarget | null>(null);
  const [responses, setResponses] = useState<{
    sponsors: SponsorResponseRow[];
    team_poll2: TeamResponseRow[];
    completion?: {
      poll1?: { done: number; total: number };
      poll2?: { done: number; total: number };
    };
  } | null>(null);
  const [leaderboard, setLeaderboard] =
    useState<LeaderboardSnapshotPayload | null>(null);

  const load = useCallback(async () => {
    if (!row?.id || !modal) return;
    setLoading(true);
    setError(null);
    try {
      if (modal === "responses") {
        const result = await dispatch(fetchCycleResponses(row.id));
        if (fetchCycleResponses.fulfilled.match(result)) {
          const data = result.payload as {
            sponsors?: SponsorResponseRow[];
            team_poll2?: TeamResponseRow[];
            completion?: {
              poll1?: { done: number; total: number };
              poll2?: { done: number; total: number };
            };
          };
          setResponses({
            sponsors: data?.sponsors ?? [],
            team_poll2: data?.team_poll2 ?? [],
            completion: data?.completion,
          });
        } else {
          throw new Error((result.payload as string) || "Failed to load");
        }
      } else {
        const result = await dispatch(fetchCycleLeaderboard(row.id));
        if (fetchCycleLeaderboard.fulfilled.match(result)) {
          const data = result.payload as LeaderboardSnapshotPayload & {
            published_at?: string;
          };
          setLeaderboard({
            period: data?.period,
            publication_state: data?.publication_state ?? "published",
            published_at: data?.published_at,
            reviewed_by_name: row.reviewed_by_name,
            rows: data?.rows ?? [],
          });
        } else {
          throw new Error((result.payload as string) || "Failed to load");
        }
      }
    } catch (e: unknown) {
      setError(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Failed to load poll data"
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, modal, row?.id, row?.reviewed_by_name]);

  useEffect(() => {
    if (!modal || !row) {
      setResponses(null);
      setLeaderboard(null);
      setError(null);
      setDrilldown(null);
      setPollResponseTarget(null);
      return;
    }
    load();
  }, [load, modal, row]);

  const baseTitle =
    modal === "responses"
      ? `View Responses · ${row?.name || row?.period}`
      : `View Leaderboard · ${row?.name || row?.period}`;
  const baseSubtitle = row
    ? `${row.period}${row.polls_run ? ` · ${row.polls_run}` : ""}`
    : undefined;

  const drilldownTitle =
    drilldown?.kind === "poll2"
      ? `Team Feedback Drilldown · ${drilldown.teamMemberName || "Team member"}`
      : `Sponsor Drilldown · ${drilldown?.sponsorName || "Sponsor"}`;

  const drilldownSubtitle =
    drilldown?.kind === "poll2"
      ? [
          "Poll 2",
          drilldown.teamMemberName,
          drilldown.sponsorName ? `→ ${drilldown.sponsorName}` : null,
          drilldown.period,
        ]
          .filter(Boolean)
          .join(" · ")
      : [drilldown?.sponsorName, drilldown?.period].filter(Boolean).join(" · ");

  const openPoll1Drilldown = (s: SponsorResponseRow) => {
    if (!row?.id || !s.sponsor_id) return;
    setDrilldown({
      kind: "poll1",
      sponsorId: s.sponsor_id,
      cycleId: s.cycle_id ?? row.id,
      period: row.period,
      sponsorName: s.sponsor_name,
    });
  };

  // Drilldown and poll-response detail now render inline in this same dialog
  // (a "second level" view with a back arrow) instead of stacking a new modal on top.
  const subView: "drilldown" | "pollResponse" | null = drilldown
    ? "drilldown"
    : pollResponseTarget
      ? "pollResponse"
      : null;

  const goBack = () => {
    setDrilldown(null);
    setPollResponseTarget(null);
  };

  const pollResponseTitleInfo = pollResponseTarget
    ? getPollResponseTitle(pollResponseTarget)
    : null;

  const activeTitle =
    subView === "drilldown"
      ? drilldownTitle
      : subView === "pollResponse"
        ? pollResponseTitleInfo?.title
        : baseTitle;

  const activeSubtitle =
    subView === "drilldown"
      ? drilldownSubtitle
      : subView === "pollResponse"
        ? pollResponseTitleInfo?.subtitle
        : baseSubtitle;

  const titleNode = subView ? (
    <span className="inline-flex min-w-0 items-center gap-2">
      <button
        type="button"
        onClick={goBack}
        aria-label="Back"
        className="-ml-1 inline-flex shrink-0 items-center rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
      >
        <ArrowLeft size={18} />
      </button>
      <span className="truncate">{activeTitle}</span>
    </span>
  ) : (
    activeTitle
  );

  const modalSize =
    subView === "drilldown"
      ? "screen-2xl"
      : modal === "leaderboard"
        ? "screen-2xl"
        : RESPONSES_MODAL_SIZE;

  const modalContentClass =
    subView === "drilldown"
      ? DRILLDOWN_MODAL_BODY_CLASS
      : modal === "leaderboard"
        ? "overflow-hidden flex flex-col max-h-[85vh]"
        : RESPONSES_MODAL_BODY_CLASS;

  const responsesFixedShell = modal === "responses" && subView !== "drilldown";
  const drilldownFixedShell = subView === "drilldown";

  return (
    <Modal
      isOpen={!!modal && !!row}
      toggle={onClose}
      size={modalSize}
      className={
        responsesFixedShell || drilldownFixedShell
          ? POLL_HISTORY_MODAL_SHELL_CLASS
          : undefined
      }
      title={titleNode}
      subtitle={activeSubtitle}
      contentClassName={modalContentClass}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {subView === "drilldown" && drilldown ? (
        <div className="flex h-full min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="flex min-h-full min-h-0 flex-1 flex-col rounded-md bg-gray-100 p-4">
              <SponsorDrilldownPanel
                sponsorId={drilldown.sponsorId}
                cycleId={drilldown.cycleId}
                period={drilldown.period}
                backTo="#"
                fillShell
              />
            </div>
          </div>
        </div>
      ) : modal === "responses" ? (
        <ResponsesModalScroll>
          {subView === "pollResponse" && pollResponseTarget ? (
            <div className="flex h-full min-h-0 flex-1 flex-col">
              <SponsorPollResponseContent target={pollResponseTarget} />
            </div>
          ) : loading ? (
            <HubPageLoading
              message="Loading responses…"
              className={hubModalLoadingClassName}
            />
          ) : error ? (
            <ResponsesModalCentered tone="error">
              {error}
            </ResponsesModalCentered>
          ) : responses ? (
            <div className="space-y-6">
              {responses.completion && (
                <div className="text-sm text-gray-600">
                  Poll 1: {responses.completion.poll1?.done ?? 0}/
                  {responses.completion.poll1?.total ?? 0}
                  {responses.completion.poll2
                    ? ` · Poll 2: ${responses.completion.poll2.done}/${responses.completion.poll2.total}`
                    : ""}
                </div>
              )}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Executive Sponsors (Poll 1)
                </h4>
                <div className="overflow-x-auto">
                  <table className={POLL_HISTORY_TABLE}>
                    <thead>
                      <tr>
                        <th>Sponsor</th>
                        <th>Theater</th>
                        <th>Completion</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {responses.sponsors.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-gray-500 py-6"
                          >
                            No sponsor responses recorded.
                          </td>
                        </tr>
                      ) : (
                        responses.sponsors.map((s, i) => (
                          <tr key={`${s.sponsor_id || s.sponsor_name}-${i}`}>
                            <td>{s.sponsor_name || "—"}</td>
                            <td>{s.theater || "—"}</td>
                            <td className="tabular-nums">
                              {s.completed_accounts ?? 0}/
                              {s.total_accounts ?? 0}
                            </td>
                            <ResponseStatusCell
                              status={s.response_status}
                              canViewResponses={!!s.sponsor_id && !!s.cycle_id}
                              onViewResponses={() => {
                                if (!s.sponsor_id || !s.cycle_id) return;
                                setPollResponseTarget({
                                  kind: "poll1",
                                  sponsorId: s.sponsor_id,
                                  cycleId: s.cycle_id,
                                  period: row?.period,
                                  sponsorName: s.sponsor_name,
                                });
                              }}
                              showDrilldown
                              canDrilldown={!!s.sponsor_id}
                              drilldownLabel={`Open sponsor drilldown for ${s.sponsor_name || "sponsor"}`}
                              onDrilldown={() => openPoll1Drilldown(s)}
                            />
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {responses.team_poll2.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Account Team (Poll 2)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className={POLL_HISTORY_TABLE}>
                      <thead>
                        <tr>
                          <th>Team member</th>
                          <th>Sponsor</th>
                          <th>Completion</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {responses.team_poll2.map((t, i) => (
                          <tr
                            key={`${t.team_member_id || t.team_member_name}-${t.sponsor_id || t.sponsor_name}-${i}`}
                          >
                            <td>{t.team_member_name || "—"}</td>
                            <td>{t.sponsor_name || "—"}</td>
                            <td className="tabular-nums">
                              {t.completed_accounts ?? 0}/
                              {t.total_accounts ?? 0}
                            </td>
                            <ResponseStatusCell
                              status={t.response_status}
                              canViewResponses={
                                !!t.team_member_id && !!t.cycle_id
                              }
                              onViewResponses={() => {
                                if (!t.team_member_id || !t.cycle_id) return;
                                setPollResponseTarget({
                                  kind: "poll2",
                                  teamMemberId: t.team_member_id,
                                  cycleId: t.cycle_id,
                                  period: row?.period,
                                  teamMemberName: t.team_member_name,
                                });
                              }}
                            />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </ResponsesModalScroll>
      ) : modal === "leaderboard" ? (
        <div className="overflow-y-auto pr-1 min-h-0 flex-1">
          <LeaderboardSnapshotView
            data={leaderboard}
            loading={loading}
            error={error}
            showDescription
            showStateBar={false}
            emptyMessage="No leaderboard data for this poll."
          />
        </div>
      ) : null}
    </Modal>
  );
};

export default PollHistoryModals;
